import React, { Component, Fragment } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { v4 as uuid } from "uuid"; // For temporary ingredient IDs in edit mode
import axios from "axios";
import {
  calculateGrams,
  UNIT_TO_GRAMS_SIMPLE,
  UNIT_LIST,
} from "./utility/unitConversions"; // Adjust path
import Select from "react-select";
import CreatableSelect from "react-select/creatable";

const config = require("../config.json");

export default class Recipe extends Component {
  constructor(props) {
    super(props);
    // Bind necessary methods for edit mode
    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleRecipeIngredientChange =
      this.handleRecipeIngredientChange.bind(this);
    this.addRecipeIngredientRow = this.addRecipeIngredientRow.bind(this);
    this.removeRecipeIngredientRow = this.removeRecipeIngredientRow.bind(this);
    this.handleEditSave = this.handleEditSave.bind(this);
    this.handleRecipeEdit = this.handleRecipeEdit.bind(this);
    // Add handleRecipeCopy if needed
  }

  state = {
    isEditMode: false,
    // Local state for editing, initialized from props
    updatedRecipe: {
      id: "",
      name: "",
      name_en: "",
      description: "",
      description_en: "",
      categories: [], // Initialize as an array
      totalPreparationTime: 0,
      activeCookingTime: 0,
      image: "",
      recipeIngredients: [], // Holds ingredient lines for editing
      // Add other recipe fields mirroring props.recipe
      // Store calculated totals from props for display, but don't edit them directly
      totalNutrition: null,
      totalKgCO2e: null,
    },
  };

  componentDidMount() {
    this.syncStateWithProps(this.props.recipe);
  }

  // Helper to sync state when props update or edit mode starts
  syncStateWithProps(recipe) {
    if (!recipe) return;

    // Ensure recipeIngredients have temporary _id for editing keys
    const recipeIngredientsWithTempId = (recipe.recipeIngredients || []).map(
      (ing) => ({
        ...ing,
        _id: ing._id || uuid(), // Add temp ID if missing
        // Ensure amounts are strings for input fields initially
        displayAmount: String(ing.displayAmount ?? ""),
        grams: String(ing.grams ?? ""),
      })
    );

    this.setState({
      updatedRecipe: {
        ...(recipe || {}), // Copy all top-level props
        recipeIngredients: recipeIngredientsWithTempId, // Use the array with _ids
        // Keep calculated fields from props for display mode
        image: recipe.image || "",
        totalPreparationTime: recipe.totalPreparationTime || 0,
        activeCookingTime: recipe.activeCookingTime || 0,
        totalNutrition: recipe.totalNutrition,
        totalKgCO2e: recipe.totalKgCO2e,
        categories: Array.isArray(recipe.categories) ? recipe.categories : [],
        url: recipe.url || "",
        portions: recipe.portions || "", // Keep as string for input, parse on save
      },
    });
  }

  fetchRecipeDetails = async (recipeId) => {
    this.setState({ loading: true });
    try {
      const res = await axios.get(
        `${config.api.invokeUrl}/cookbook/recipes/${recipeId}/details`
      ); // Adjust endpoint if needed
      const recipeDetails = res.data || [];
      this.setState({
        updatedRecipe: recipeDetails,
      });
    } catch (err) {
      console.error("Error fetching recipe details:", err);
      this.setState({ loading: false });
    }
  };

  // --- Edit Mode Handlers ---

  handleRecipeEdit = (event) => {
    event.preventDefault();
    // Sync state with latest props before entering edit mode
    if (!this.state.updatedRecipe.recipeIngredients.length) {
      this.fetchRecipeDetails(this.state.updatedRecipe.id);
    }

    this.setState({ isEditMode: true });
  };

  handleEditSave = (event) => {
    event.preventDefault();
    this.setState({ isEditMode: false });
    // Pass the locally edited state up to the parent handler
    this.props.handleUpdateRecipe(this.state.updatedRecipe);
  };

  handleCancelEdit = (event) => {
    event.preventDefault();
    // Optionally reset local state to original props here if needed,
    // but simply exiting edit mode often suffices.
    this.setState({ isEditMode: false });
  };

  // --- Ingredient Row Management (Edit Mode) ---
  // These are very similar to RecipeAdmin, but operate on state.updatedRecipe

  addRecipeIngredientRow() {
    const newIngredientRow = {
      _id: uuid(),
      ingredientId: "",
      ingredientName: "",
      displayAmount: "",
      displayUnit: "g",
      grams: "",
    };
    this.setState((prevState) => ({
      updatedRecipe: {
        ...prevState.updatedRecipe,
        recipeIngredients: [
          ...prevState.updatedRecipe.recipeIngredients,
          newIngredientRow,
        ],
      },
    }));
  }

  removeRecipeIngredientRow(index) {
    this.setState((prevState) => ({
      updatedRecipe: {
        ...prevState.updatedRecipe,
        recipeIngredients: prevState.updatedRecipe.recipeIngredients.filter(
          (_, i) => i !== index
        ),
      },
    }));
  }

  handleRecipeIngredientChange(index, fieldName, value) {
    this.setState((prevState) => {
      const updatedIngredients = [...prevState.updatedRecipe.recipeIngredients];
      const currentIngredient = { ...updatedIngredients[index] };

      let newGrams = currentIngredient.grams;

      if (fieldName === "ingredientId") {
        // Use ingredients list passed via props
        const selectedIngredient = this.props.ingredients.find(
          (ing) => ing.id === value
        );
        currentIngredient.ingredientId = value || "";
        currentIngredient.ingredientName = selectedIngredient
          ? selectedIngredient.name
          : "";
      } else {
        currentIngredient[fieldName] = value;
      }

      if (fieldName !== "grams") {
        const calculated = calculateGrams(
          currentIngredient.displayAmount,
          currentIngredient.displayUnit
        );
        if (calculated !== "") {
          newGrams = calculated;
        } else if (
          fieldName === "displayUnit" ||
          fieldName === "displayAmount"
        ) {
          newGrams = "";
        }
      } else {
        newGrams = value;
      }
      currentIngredient.grams = newGrams;

      updatedIngredients[index] = currentIngredient;

      return {
        updatedRecipe: {
          ...prevState.updatedRecipe,
          recipeIngredients: updatedIngredients,
        },
      };
    });
  }

  // Handles top-level recipe field changes in edit mode
  handleInputChange = (event) => {
    const target = event.target;
    const value = target.type === "checkbox" ? target.checked : target.value;
    const name = target.name;

    this.setState((prevState) => ({
      updatedRecipe: {
        ...prevState.updatedRecipe,
        [name]: value,
      },
    }));
  };

  handleCategoriesChange = (selectedOptions) => {
    const categoriesArray = selectedOptions
      ? selectedOptions.map((option) => option.value)
      : [];
    // Update this.state.newRecipe.categories or this.state.updatedRecipe.categories
    this.setState((prevState) => ({
      updatedRecipe: {
        // or updatedRecipe
        ...prevState.updatedRecipe, // or updatedRecipe
        categories: categoriesArray,
      },
    }));
  };

  // --- Render ---

  renderEditModeIngredientForm(ingredientItem, index) {
    // Prepare options from props.ingredients
    const ingredientSelectOptions = this.props.ingredients.map((ing) => ({
      value: ing.id,
      label: `${ing.name} (${ing.category || "Ukategoriseret"})`,
    }));

    const currentIngredientValue = ingredientSelectOptions.find(
      (option) => option.value === ingredientItem.ingredientId
    );

    const unitOptions = UNIT_LIST.map((unit) => (
      <option key={unit} value={unit}>
        {unit}
      </option>
    ));

    return (
      <div key={ingredientItem._id} className="box recipe-ingredient-row mb-3">
        <div className="columns is-mobile is-vcentered">
          {/* === Ingredient Selection (react-select) === */}
          <div className="column is-4">
            <div className="field">
              <label className="label is-small">Ingrediens</label>
              <div className="control">
                <Select
                  options={ingredientSelectOptions}
                  value={currentIngredientValue}
                  onChange={(selectedOption) =>
                    this.handleRecipeIngredientChange(
                      index,
                      "ingredientId",
                      selectedOption ? selectedOption.value : ""
                    )
                  }
                  placeholder="Søg/vælg ingrediens..."
                  isClearable
                  isSearchable
                  // Pass loading state if available via props?
                  noOptionsMessage={() => "Ingen ingredienser fundet"}
                />
              </div>
            </div>
          </div>

          {/* Display Amount */}
          <div className="column is-2">
            {/* ... (same input) ... */}
            <div className="field">
              <label className="label is-small">Mængde</label>
              <div className="control">
                <input
                  className="input is-small"
                  type="number"
                  step="any"
                  placeholder="fx 2"
                  value={ingredientItem.displayAmount}
                  onChange={(e) =>
                    this.handleRecipeIngredientChange(
                      index,
                      "displayAmount",
                      e.target.value
                    )
                  }
                />
              </div>
            </div>
          </div>

          {/* Display Unit */}
          <div className="column is-2">
            {/* ... (same select) ... */}
            <div className="field">
              <label className="label is-small">Enhed</label>
              <div className="control">
                <div className="select is-small is-fullwidth">
                  <select
                    value={ingredientItem.displayUnit}
                    onChange={(e) =>
                      this.handleRecipeIngredientChange(
                        index,
                        "displayUnit",
                        e.target.value
                      )
                    }
                  >
                    {unitOptions}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Grams (Calculated/Manual) */}
          <div className="column is-3">
            {/* ... (same input) ... */}
            <div className="field">
              <label className="label is-small">Gram (til beregning)</label>
              <div className="control">
                <input
                  className="input is-small"
                  type="number"
                  step="any"
                  placeholder="Auto/Manuel"
                  value={ingredientItem.grams}
                  onChange={(e) =>
                    this.handleRecipeIngredientChange(
                      index,
                      "grams",
                      e.target.value
                    )
                  }
                  title="Beregnes automatisk, men kan overskrives manuelt"
                />
              </div>
            </div>
          </div>

          {/* Remove Button */}
          <div className="column is-1">
            {/* ... (same button) ... */}
            <button
              type="button"
              className="button is-danger is-small is-outlined mt-4"
              onClick={() => this.removeRecipeIngredientRow(index)}
              aria-label="Fjern ingrediens"
            >
              {" "}
              ×{" "}
            </button>
          </div>
        </div>
      </div>
    );
  }

  render() {
    const { handleDeleteRecipe, isAdmin } = this.props; // Destructure props
    const { updatedRecipe, isEditMode } = this.state; // Destructure state

    // Handle case where recipe data might be initially undefined
    if (!updatedRecipe || !updatedRecipe.id) {
      return <div className="box notification">Opskriftsdata mangler.</div>;
    }
    const s3ImageUrlPrefix = config.s3.recipeImagesUrl;

    let totalGrams = 0;
    for (const ingredient of updatedRecipe.recipeIngredients) {
      totalGrams += ingredient.grams;
    }

    return (
      <div className={`box notification ${this.props.className || ""}`}>
        {isAdmin && (
          <Fragment>
            {!isEditMode && ( // Show edit icon only in display mode
              <a
                href="#"
                onClick={this.handleRecipeEdit}
                className="product-edit-icon"
                style={{
                  position: "absolute",
                  top: "10px",
                  right: "40px",
                  cursor: "pointer",
                }}
              >
                <FontAwesomeIcon icon="edit" />
              </a>
            )}
            <button
              onClick={(event) => handleDeleteRecipe(updatedRecipe.id, event)}
              className="delete"
            ></button>
          </Fragment>
        )}

        {isEditMode ? (
          // --- Edit Mode Form ---
          <div>
            <h4 className="title is-6">Rediger Opskrift</h4>
            {/* Basic Recipe Info */}
            <div className="columns">
              <div className="column is-half">
                <div className="field">
                  <label className="label is-small">Navn (Dansk)</label>
                  <div className="control">
                    <input
                      name="name"
                      className="input is-small"
                      type="text"
                      value={updatedRecipe.name}
                      onChange={this.handleInputChange}
                    />
                  </div>
                </div>

                <div className="field">
                  <label className="label is-small">Beskrivelse (Dansk)</label>
                  <div className="control">
                    <textarea
                      name="description"
                      className="textarea is-small"
                      value={updatedRecipe.description}
                      onChange={this.handleInputChange}
                    />
                  </div>
                </div>
              </div>
              <div className="column is-half">
                <div className="field">
                  <label className="label is-small">Navn (Engelsk)</label>
                  <div className="control">
                    <input
                      name="name_en"
                      className="input is-small"
                      type="text"
                      value={updatedRecipe.name_en}
                      onChange={this.handleInputChange}
                    />
                  </div>
                </div>
                <div className="field">
                  <label className="label is-small">
                    Beskrivelse (Engelsk)
                  </label>
                  <div className="control">
                    <textarea
                      name="description_en"
                      className="textarea is-small"
                      value={updatedRecipe.description_en}
                      onChange={this.handleInputChange}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="field">
              <label className="label is-small">Kategorier</label>
              <CreatableSelect
                isMulti
                options={[]}
                value={
                  this.state.updatedRecipe?.categories?.map((cat) => ({
                    value: cat,
                    label: cat,
                  })) ?? []
                }
                onChange={this.handleCategoriesChange}
                placeholder="Tilføj kategorier..."
                formatCreateLabel={(inputValue) => `Opret "${inputValue}"`}
              />
            </div>
            <div className="field">
              <label className="label is-small">Billede</label>
              <div className="select is-small is-fullwidth">
                <select
                  name="image"
                  value={updatedRecipe.image || ""}
                  onChange={this.handleInputChange}
                >
                  <option value="">Intet billede</option>
                  {(this.props.recipeImages || []).map((img) => (
                    <option key={img.Key} value={img.Key}>
                      {img.Key}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="columns">
              <div className="column is-half">
                <div className="field">
                  <label className="label is-small">
                    Aktiv Tilberedningstid
                  </label>
                  <input
                    name="activeCookingTime"
                    className="input is-small"
                    type="text"
                    value={updatedRecipe.activeCookingTime}
                    onChange={this.handleInputChange}
                  />
                </div>
              </div>
              <div className="column is-half">
                <div className="field">
                  <label className="label is-small">
                    Total Forberedelsestid
                  </label>
                  <input
                    name="totalPreparationTime"
                    className="input is-small"
                    type="text"
                    value={updatedRecipe.totalPreparationTime}
                    onChange={this.handleInputChange}
                  />
                </div>
              </div>
            </div>

            <div className="columns">
              <div className="column is-half">
                <div className="field">
                  <label className="label is-small">Opskrifts URL</label>
                  <input
                    name="url"
                    className="input is-small"
                    type="url"
                    value={updatedRecipe.url}
                    onChange={this.handleInputChange}
                  />
                </div>
              </div>
              <div className="column is-half">
                <div className="field">
                  <label className="label is-small">Antal Portioner</label>
                  <input
                    name="portions"
                    className="input is-small"
                    type="number"
                    min="1"
                    value={updatedRecipe.portions}
                    onChange={this.handleInputChange}
                    required
                  />
                </div>
              </div>
            </div>
            {/* Dynamic Ingredient List Section */}
            <h5 className="title is-6 mt-3">Ingredienser</h5>
            {updatedRecipe.recipeIngredients.map((item, index) =>
              this.renderEditModeIngredientForm(item, index)
            )}
            <button
              type="button"
              className="button is-info is-outlined is-small mb-3"
              onClick={this.addRecipeIngredientRow}
              disabled={
                !this.props.ingredients || this.props.ingredients.length === 0
              }
            >
              + Tilføj Ingrediens Linje
            </button>
            {!this.props.ingredients ||
              (this.props.ingredients.length === 0 && (
                <p className="has-text-danger is-size-7">
                  Ingrediensliste mangler.
                </p>
              ))}

            {/* ID Display and Action Buttons */}
            <p
              className="product-id"
              style={{ fontSize: "0.8em", color: "#777", marginTop: "10px" }}
            >
              ID: {updatedRecipe.id}
            </p>
            <div className="buttons are-small mt-2">
              <button
                type="button"
                className="button is-success"
                onClick={this.handleEditSave}
              >
                Gem Ændringer
              </button>
              <button
                type="button"
                className="button is-light"
                onClick={this.handleCancelEdit} // Add a cancel handler
              >
                Annuller
              </button>
            </div>
          </div>
        ) : (
          // --- Display Mode ---
          <div>
            <p className="product-title title is-5">{this.props.recipe.name}</p>

            <div>
              {updatedRecipe.image && (
                <figure className="image is-128x128 mb-3">
                  <img
                    src={`${s3ImageUrlPrefix}${updatedRecipe.image}`}
                    alt={updatedRecipe.name}
                  />
                </figure>
              )}
              <p>
                <b>Kategorier:</b>
                {updatedRecipe.categories &&
                updatedRecipe.categories.length > 0 ? (
                  <span className="tags ml-1">
                    {updatedRecipe.categories.map((cat) => (
                      <span key={cat} className="tag is-info">
                        {cat}
                      </span>
                    ))}
                  </span>
                ) : (
                  <i className="has-text-grey-light ml-1">Ingen kategorier</i>
                )}
              </p>
              {/* ... (Display ingredient list, calculated totals, ID) ... */}
            </div>
            <p className="is-size-7 has-text-grey mb-2">
              ({this.props.recipe.name_en})
            </p>
            <p>
              <b>Beskrivelse:</b>{" "}
              {this.props.recipe.description || (
                <i className="has-text-grey-light">Ingen beskrivelse</i>
              )}
            </p>
            <p className="is-size-7 has-text-grey mb-3">
              ({this.props.recipe.description_en || "No description"})
            </p>

            <p>
              <b>URL:</b>{" "}
              {updatedRecipe.url ? (
                <a
                  href={updatedRecipe.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {updatedRecipe.url}
                </a>
              ) : (
                <i className="has-text-grey-light">N/A</i>
              )}
            </p>
            <p>
              <b>Portioner:</b>{" "}
              {updatedRecipe.portions || (
                <i className="has-text-grey-light">N/A</i>
              )}
            </p>

            {/* Add other display fields like servings, prepTime etc. */}

            <h6 className="title is-6 mt-3">Ingredienser:</h6>
            {updatedRecipe.recipeIngredients &&
            updatedRecipe.recipeIngredients.length > 0 ? (
              <>
                <ul
                  style={{
                    listStyle: "disc",
                    marginLeft: "20px",
                    marginBottom: "15px",
                  }}
                >
                  {updatedRecipe.recipeIngredients.map((ing) => (
                    <li
                      style={{ alignItems: "center" }}
                      key={ing._id || ing.ingredientId}
                      className="is-size-7 d-flex flex-row"
                    >
                      {ing.image && (
                        <figure
                          className="image is-32x32 mb-3 mr-5"
                          style={{ marginRight: 10 }}
                        >
                          <img
                            src={`${config.s3.ingredientImagesUrl}${ing.image}`}
                            alt={updatedRecipe.name}
                          />
                        </figure>
                      )}
                      {ing.displayAmount} {ing.displayUnit}{" "}
                      {ing.ingredientName || (
                        <i className="has-text-grey-light">
                          Ukendt ({ing.ingredientId})
                        </i>
                      )}
                      <span className="has-text-grey ml-2">({ing.grams}g)</span>{" "}
                      {/* Show calculated grams */}
                    </li>
                  ))}
                </ul>
                <div>
                  <b>Total vægt: </b>
                  {totalGrams + "g"}
                </div>
                <br />
              </>
            ) : (
              <button
                type="button"
                className="button is-link is-light"
                onClick={() => {
                  this.fetchRecipeDetails(updatedRecipe.id);
                }}
              >
                Se ingredienser
              </button>
            )}

            {/* Display Calculated Totals (fetched from backend) */}
            {this.props.recipe.totalKgCO2e !== null &&
              this.props.recipe.totalKgCO2e !== undefined && (
                <p className="is-size-7 mt-3">
                  <b>Total CO2e (beregnet):</b>{" "}
                  {this.props.recipe.totalKgCO2e.toFixed(2)} kg CO2e
                </p>
              )}
            {this.props.recipe.totalNutrition && (
              <div className="is-size-7 mt-1">
                <b>Total Næring (beregnet):</b>
                <ul style={{ listStyle: "none", paddingLeft: "10px" }}>
                  <li>
                    Kcal: {this.props.recipe.totalNutrition.kcal?.toFixed(0)}
                  </li>
                  <li>
                    Protein:{" "}
                    {this.props.recipe.totalNutrition.protein?.toFixed(1)} g
                  </li>
                  <li>
                    Kulhydrat:{" "}
                    {this.props.recipe.totalNutrition.carbohydrates?.toFixed(1)}{" "}
                    g
                  </li>
                  <li>
                    Fedt: {this.props.recipe.totalNutrition.fat?.toFixed(1)} g
                  </li>
                  {/* Add other nutrition totals */}
                </ul>
              </div>
            )}

            {updatedRecipe.kgCO2ePerPortion !== null &&
              updatedRecipe.kgCO2ePerPortion !== undefined && (
                <p className="is-size-7 mt-3">
                  <b>CO2e pr. Portion:</b>{" "}
                  {updatedRecipe.kgCO2ePerPortion.toFixed(3)} kg CO2e
                </p>
              )}
            {updatedRecipe.nutritionPerPortion && (
              <div className="is-size-7 mt-1">
                <b>Næring pr. Portion:</b>
                <ul style={{ listStyle: "none", paddingLeft: "10px" }}>
                  <li>
                    Kcal: {updatedRecipe.nutritionPerPortion.kcal?.toFixed(0)}
                  </li>
                </ul>
              </div>
            )}

            <p
              className="product-id"
              style={{ fontSize: "0.8em", color: "#777", marginTop: "10px" }}
            >
              ID: {updatedRecipe.id}
            </p>
          </div>
        )}
      </div>
    );
  }
}
